import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import {
  LayoutActionLoadError,
  LayoutActionSaveSuccess,
  LayoutActionVersionError
} from '@shared/store/actions/layout.action';

import { CreateStoreStockRecallService } from '../../service/create-store-stock-recall.service';
import {
  cancelStoreStockRecallRequest,
  createStoreStockRecallPackingRequest,
  createStoreStockRecallRequest,
  deleteDraftStoreStockRecallPackingRequest,
  draftStoreStockRecallPackingRequest,
  errorCreateStoreStockRecallResponse
} from './create-stock-recall.action';

@Injectable()
export class CreateStockRecallEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly logger: NGXLogger,
    private service: CreateStoreStockRecallService
  ) {}

  createStoreStockRecallRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createStoreStockRecallRequest),
      tap(action =>
        this.logger.debug('@Effect Create Store Stock Recall Request: ' + JSON.stringify(action.payload))
      ),
      switchMap(action => {
        return this.service.createStoreStockRecall(action.payload).pipe(
          map(
            () =>
              new LayoutActionSaveSuccess({
                isSuccess: true,
                title: 'Success',
                message: `The Store Stock Recall has been created.`
              })
          ),
          catchError(err => {
            if (err && err.error && err.error.code === '09000') {
              return of(errorCreateStoreStockRecallResponse({ items: err.error.items }));
            }
            of(new LayoutActionLoadError(err));
          })
        );
      })
    );
  });

  createStoreStockRecallPackingRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createStoreStockRecallPackingRequest),
      tap(action =>
        this.logger.debug('@Effect Create Store Stock Recall Packing Request: ' + JSON.stringify(action.payload))
      ),
      switchMap(action => {
        return this.service.createStoreStockRecall(action.payload).pipe(
          map(
            () =>
              new LayoutActionSaveSuccess({
                isSuccess: true,
                title: 'Success',
                message: `The Store Stock Recall step packing has been created.`
              })
          ),
          catchError(err => of(new LayoutActionLoadError(err)))
        );
      })
    );
  });

  cancelStockRecallRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cancelStoreStockRecallRequest),
      tap(action =>
        this.logger.debug('@Effect Cancel Store Stock Recall Request: ' + JSON.stringify(action.payload))
      ),
      switchMap(action => {
        return this.service.cancelStoreStockRecall(action.payload).pipe(
          map(
            () =>
              new LayoutActionSaveSuccess({
                isSuccess: true,
                title: 'Success',
                message: 'Store Stock Recall has been cancelled.'
              })
          ),
          catchError(error => of(new LayoutActionLoadError(error)))
        );
      })
    );
  });

  draftStoreStockRecallRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(draftStoreStockRecallPackingRequest),
      tap(action => this.logger.debug('@Effect Draft Store Stock Recall Request: ' + JSON.stringify(action.payload))),
      switchMap(action => {
        return this.service.draftStoreStockRecall(action.payload).pipe(
          map(
            () =>
              new LayoutActionSaveSuccess({
                isSuccess: true,
                title: 'Success',
                message: `Store Stock Recall has been saved.`
              })
          ),
          catchError(err => this.errorHandling(err))
        );
      })
    );
  });

  deleteDraftStoreStockRecallRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteDraftStoreStockRecallPackingRequest),
      tap(action =>
        this.logger.debug('@Effect Delete Draft Store Stock Recall Request: ' + JSON.stringify(action.id))
      ),
      switchMap(action => {
        return this.service.deleteDraftStoreStockRecall(action.id).pipe(
          map(
            () =>
              new LayoutActionSaveSuccess({
                isSuccess: true,
                title: 'Success',
                message: `Store Stock Recall has been deleted.`
              })
          ),
          catchError(err => this.errorHandling(err))
        );
      })
    );
  });

  errorHandling(err: any) {
    return err.error && err.error.code === '00003'
      ? of(new LayoutActionVersionError(true))
      : of(new LayoutActionLoadError(err));
  }
}
